import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface ScrollXProps {
  children: ReactNode;
  className?: string;
  /** Hint shown under the content when it can scroll horizontally */
  hint?: string;
}

/**
 * Horizontally scrollable wrapper that only advertises itself when there is
 * actually something off-screen — keeps phones from hiding columns silently.
 */
export const ScrollX = ({ children, className, hint = "Swipe for more columns" }: ScrollXProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState(false);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const overflow = el.scrollWidth - el.clientWidth;
      setScrollable(overflow > 4);
      setAtEnd(el.scrollLeft >= overflow - 4);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    el.addEventListener("scroll", update, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", update);
    };
  }, [children]);

  return (
    <div className="relative">
      <div
        ref={ref}
        className={cn("overflow-x-auto", scrollable && !atEnd && "scroll-fade-x", className)}
      >
        {children}
      </div>
      {scrollable && !atEnd && (
        <p className="mt-1 pr-1 text-right text-[11px] text-muted-foreground sm:hidden">{hint} →</p>
      )}
    </div>
  );
};

export default ScrollX;
